import React, { PropTypes } from "react";
import { ButtonToolbar } from "react-bootstrap";
import { FormattedDate, FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import {
  EditButton
} from "../../../../../../common";
import { DataTable } from "../../../../../../common/DataTable";
import BasicMemberEmploymentFields from "./BasicEmploymentFields";

import * as dataActions from "../../../../../../../utils/data/dataActions";
import * as types from "../../../../../../../utils/data/dataActionTypes";
import { EMPLOYMENTS } from "../../../../../../../utils/data/models";

import moment from "moment";
import { toast } from "react-toastify";
import { ADMINISTRATOR, AGENT, DIRECTOR, FINANCE, FINTECH, HR_CLERK, INFORMATION_AGENT, MANAGER, PLAN_ADMINISTRATOR } from "../../../../../../../utils/Roles";
import userHasAccess from "../../../../../../../utils/userHasAccess";
import { toLocaleDate } from "../../../../../../common/dateHelper";
import { toCamelCase } from "../../../../../../common/form/DBEnumStringHelper";
import { ACTIVE, IMPLEMENTATION, INACTIVE, TERMINATED } from "../../../../enums/clientStatusTypes";

class EmploymentsDataTable extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.createEmptyEmploymentModel = this.createEmptyEmploymentModel.bind(
      this
    );
    this.anticipatedErrors = ["overlappingMemberEmploymentError","earlierMemberEmploymentError"];
    this.getData = this.getData.bind(this);
    this.createEmployment = this.createEmployment.bind(this);
    this.generateAddButton = this.generateAddButton.bind(this);
    this.getCols = this.getCols.bind(this);
    this.generateActionButtons = this.generateActionButtons.bind(this);
    this.updateEmployment = this.updateEmployment.bind(this);
    this.getViewOnlyForClientStatusType = this.getViewOnlyForClientStatusType.bind(this);

    this.viewEmploymentForImplementation = !userHasAccess(FINANCE);
    this.viewEmploymentForActive = !userHasAccess([DIRECTOR, MANAGER, AGENT, PLAN_ADMINISTRATOR, ADMINISTRATOR, FINTECH, HR_CLERK], true);
    this.viewEmploymentForInactive = !userHasAccess([ADMINISTRATOR, FINTECH, DIRECTOR, MANAGER, AGENT], true);
    this.viewEmploymentForTerminated = !userHasAccess(FINTECH);
  }

  componentWillUnmount() {
    this.props.dataActions.clearDataList(EMPLOYMENTS);
  }

  getViewOnlyForClientStatusType() {
    const { clientStatusType, activeMember } = this.props;

    const isMemberArchived = activeMember && activeMember.isArchived && !userHasAccess(FINTECH);

    switch (clientStatusType) {
      case IMPLEMENTATION.value:
        return this.viewEmploymentForImplementation || isMemberArchived;
      case ACTIVE.value:
        return this.viewEmploymentForActive || isMemberArchived;
      case INACTIVE.value:
        return this.viewEmploymentForInactive || isMemberArchived;
      case TERMINATED.value:
        return this.viewEmploymentForTerminated || isMemberArchived;
    }
  }

  getData(offset, limit, q, sortBy, filters) {
    return this.props.dataActions.getDataList(
      EMPLOYMENTS,
      offset,
      limit,
      q,
      sortBy,
      filters,
      null,
      { memberId: this.props.memberId }
    );
  }

  createEmptyEmploymentModel() {
    return {
      memberId: this.props.memberId,
      startDate: "",
      endDate: "",
      employmentType: "",
      defineEndDate: null,
    };
  }

  _cleanValues(values) {
    values.endDate = typeof values.endDate == "string" && values.endDate.trim() === "" ? null : values.endDate;
  }

  createEmployment(values) {
    this._cleanValues(values);
    this.props.dataActions.createDataItem(EMPLOYMENTS, values).then((data) => {
      if (
        data.type === types.CREATE_DATA_ITEM_FAILURE
      ) {
        const errorCode = data.payload && data.payload.error && data.payload.error.data && data.payload.error.data.error;
        if (errorCode && this.anticipatedErrors.includes(toCamelCase(errorCode))) {
          toast(<FormattedMessage id={`employments.datatable.errors.${toCamelCase(errorCode)}`} />, {
            type: toast.TYPE.ERROR,
            autoClose: false,
          });
        }
        else {
          toast(<FormattedMessage id="genericSaveError" />, {
            type: toast.TYPE.ERROR,
            autoClose: false,
          });
        }
      } else {
        this.getData(
          this.props.offset,
          this.props.limit,
          this.props.q,
          this.props.sortBy,
          this.props.filters
        );
      }
    });
  }

  generateAddButton(canAddEmployment) {
    const viewOnly = userHasAccess([INFORMATION_AGENT], true) || !canAddEmployment;
    return (
      <div>
        {!viewOnly && (
          <EditButton
            allowSubmission={!viewOnly}
            title={<FormattedMessage id="employments.form.title" />}
            label={<FormattedMessage id="common.add" />}
            add={true}
            form={BasicMemberEmploymentFields}
            data={this.createEmptyEmploymentModel()}
            editFunction={this.createEmployment}
          />
        )}
      </div>
    );
  }

  getCols() {
    return [
      {
        name: <FormattedMessage id="memberCoverages.datatable.columns.date" />,
        sortKeys: ["startDate"],
        display: (employment) => {
          if (employment.endDate) {
            return (
              <span>
                <FormattedDate
                  value={toLocaleDate(employment.startDate)}
                  year="numeric"
                  month="long"
                  day="2-digit"
                  timeZone="UTC"
                />
                <span> - </span>
                <FormattedDate
                  value={toLocaleDate(employment.endDate)}
                  year="numeric"
                  month="long"
                  day="2-digit"
                  timeZone="UTC"
                />
              </span>
            );
          }
          return (
            <span>
              <FormattedMessage id="memberCoverages.datatable.columns.periodFrom" />
              <FormattedDate
                value={employment.startDate}
                year="numeric"
                month="long"
                day="2-digit"
                timeZone="UTC"
              />
            </span>
          );
        },
        widthPct: 45,
      },
      {
        name: <FormattedMessage id="employments.datatable.columns.employmentType" />,
        display: (employment) => (
          <FormattedMessage
            id={`employments.employmentTypes.${employment.employmentType.toLowerCase()}`}
          />
        ),
        widthPct: 45,
      },
      {
        name: <FormattedMessage id="datatable.actions" />,
        display: (employment) => this.generateActionButtons(employment),
        widthPct: 10,
      },
    ];
  }

  updateEmployment(values) {
    this._cleanValues(values);
    this.props.dataActions.updateDataItem(EMPLOYMENTS, values).then((data) => {
      if (
        data.type === types.UPDATE_DATA_ITEM_FAILURE
      ) {
        const errorCode = data.payload && data.payload.error && data.payload.error.data && data.payload.error.data.error;
        if (errorCode && this.anticipatedErrors.includes(toCamelCase(errorCode))) {
          toast(<FormattedMessage id={`employments.datatable.errors.${toCamelCase(errorCode)}`} />, {
            type: toast.TYPE.ERROR,
            autoClose: false,
          });
        }
        else {
          toast(<FormattedMessage id="genericSaveError" />, {
            type: toast.TYPE.ERROR,
            autoClose: false,
          });
        }
      } else {
        this.getData(
          this.props.offset,
          this.props.limit,
          this.props.q,
          this.props.sortBy,
          this.props.filters
        );
      }
    });
  }

  generateActionButtons(employment) {
    const viewOnly = this.getViewOnlyForClientStatusType();
    return (
      <ButtonToolbar key={employment.memberEmploymentId}>
        <EditButton
          title={
            <FormattedMessage
              id={`employments.datatable.actions.${viewOnly ? "view" : "edit"
                }`}
            />
          }
          label={
            <FormattedMessage id={viewOnly ? "common.view" : "common.edit"} />
          }
          form={BasicMemberEmploymentFields}
          props={{ disabled: viewOnly }}
          allowSubmission={!viewOnly}
          data={{
            ...employment,
            defineEndDate: employment.endDate ? true : null,
            startDate: moment(new Date(employment.startDate).toISOString()),
          }}
          editFunction={this.updateEmployment}
        />
      </ButtonToolbar>
    );
  }

  render() {
    const canAddEmployment = !this.getViewOnlyForClientStatusType();
    return (
      <div>
        <DataTable
          ref="dataTable"
          title={<FormattedMessage id="employments.title" />}
          getData={this.getData}
          cols={this.getCols()}
          q={this.props.q}
          filters={this.props.filters}
          offset={this.props.offset}
          data={this.props.items}
          rowsPerPage={this.props.limit}
          totalRows={this.props.total}
          sortBy={this.props.sortBy}
          addButton={this.generateAddButton(canAddEmployment)}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  const data = state.data.employments || {};
  const pagination = data.pagination || {};
  const activeMember = state.data.members.active;
  const memberId = activeMember.memberId;
  const activeClient = state.data.clients && state.data.clients.active || {};
  return {
    activeMember,
    memberId,
    active: data.active || {},
    authLanguageCode: state.auth.user.languageCode,
    limit: pagination.limit || 100,
    offset: pagination.offset || 0,
    sortBy: data.sortBy || ["startDate:desc"],
    q: data.q,
    filters: data.filters || [
      `memberEmployments.memberId==${memberId}`,
    ],
    total: pagination.totalCount || 0,
    items: data.items || null,
    clientStatusType: activeClient.clientStatusType
  };
}

EmploymentsDataTable.propTypes = {
  dataActions: PropTypes.object,
  memberEmploymentId: PropTypes.number,
  memberId: PropTypes.number,
  limit: PropTypes.number,
  offset: PropTypes.number,
  sortBy: PropTypes.array,
  total: PropTypes.number,
  q: PropTypes.string,
  filters: PropTypes.array,
  items: PropTypes.array,
  clientStatusType: PropTypes.string,
  activeMember: PropTypes.object
};

function mapDispatchToProps(dispatch) {
  return {
    dataActions: bindActionCreators(dataActions, dispatch),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EmploymentsDataTable);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/members/member/employments/EmploymentsDataTable.js